/**
 * @class Flyout
 * @classdesc A instance of the a drawer flyout which can be populated with content
 */
export default class Flyout extends Core.Component {
  constructor(el, context) {
    super(el, context);

    this.flyoutHeaderWrapper = this.el.querySelector('[data-flyout-header-wrapper]');
    this.flyoutHeader = this.el.querySelector('[data-flyout-header]');
    this.flyoutHeading = this.el.querySelector('[data-flyout-heading]');
    this.flyoutNotification = this.el.querySelector('[data-flyout-notification]');
    this.flyoutCloseBtn = this.el.querySelector('[data-flyout-close-btn]');
    this.flyoutContent = this.el.querySelector('[data-flyout-content]');
    this.flyoutFooter = this.el.querySelector('[data-flyout-footer]');

    this.flyoutContent = this.el.querySelector('[data-flyout-content]');
    this.flyoutAnimation = Core.Animations.transition({ el: this.el, state: 'closed' });
    this.flyoutNotificationAnimation = Core.Animations.transition({ el: this.flyoutNotification, state: 'closed' });

    this.events.register(this.flyoutCloseBtn, 'click', () => this.parent.closeFlyout(this));
    this.events.register(this.el, 'click', e => e.stopPropagation());

    this.state = {
      open: false,
      trigger: null,
    };
  }

  onDismount() {
    super.onDismount();

    Core.Utils.scrollUnlock(this.el);
    Core.A11y.removeTrapFocus();
  }

  /**
   * Opens the flyout, traps focus within it, and locks the scroll behaviour
   * on the site (except for this flyout).
   *
   * @param {Boolean} loading If true, will clear the content and set the state to loading
   */
  open(trigger) {
    this.setState({
      open: true,
      trigger,
    });

    this.flyoutAnimation
      .animateTo('open')
      .then(() => this.focus());
  }

  focus() {
    Core.Utils.scrollLock(this.el, this.flyoutContent);

    // Trap focus so that all items within the content are now accessible through keyboard
    Core.A11y.trapFocus(this.el);
  }

  clear() {
    this.clearHeading();
    this.clearNotification();
    this.clearContent();
    this.clearFooter();
  }

  clearHeading() {
    this.flyoutHeader.setAttribute('hidden', '');
    this.flyoutHeading.innerHTML = '';

    if (this.flyoutNotification.hasAttribute('hidden')) {
      this.flyoutHeaderWrapper.setAttribute('hidden', '');
    }
  }

  clearNotification(animate = false) {
    return this.flyoutNotificationAnimation
      .animateTo(
        'closed',
        {
          onStart: () => this.flyoutNotification.style.setProperty('--notification-height', ''),
          force: !animate,
        },
      )
      .then(() => {
        this.flyoutNotification.setAttribute('hidden', '');
        this.flyoutNotification.innerHTML = '';

        if (this.flyoutHeader.hasAttribute('hidden')) {
          this.flyoutHeaderWrapper.setAttribute('hidden', '');
        }
      });
  }

  clearContent() {
    this.flyoutContent.innerHTML = '';
  }

  clearFooter() {
    this.flyoutFooter.setAttribute('hidden', '');
    this.flyoutFooter.innerHTML = '';
  }

  /**
   * Updates the content of the flyout to contain the DOM in contentHTML. If the element is already
   * in the DOM, that element will be moved inside of this flyout and will no longer exist in its
   * original location.
   *
   * @param {HTMLNode} contentNode The content to be placed in the flyout (will be moved in the DOM)
   */
  populateHeading(contentNode, replace = false) {
    if (replace) {
      this.flyoutHeading.innerHTML = '';
    }

    this.flyoutHeading.appendChild(contentNode);
    this.flyoutHeader.removeAttribute('hidden');
    this.flyoutHeaderWrapper.removeAttribute('hidden');

    if (this.state.open) {
      // Trap focus again so that all items within the content are now accessible through keyboard
      Core.A11y.trapFocus(this.el);
    }
  }

  populateNotification(contentNode, replace = false, animate = false, timeout = 0) {
    if (replace) {
      this.flyoutNotification.innerHTML = '';
    }

    this.flyoutNotification.appendChild(contentNode);
    this.flyoutNotification.removeAttribute('hidden');
    this.flyoutHeaderWrapper.removeAttribute('hidden');

    const animation = Promise.resolve();
    const scrollheight = Array
      .from(this.flyoutNotification.children)
      .reduce((acc, child) => (acc + child.scrollHeight), 0);

    if (this.state.open) {
      // Trap focus again so that all items within the content are now accessible through keyboard
      Core.A11y.trapFocus(this.el);

      // Resize the element
      this.flyoutNotification.style.setProperty('--notification-height', `${scrollheight}px`);
    } else {
      animation.then(() => (
        this.flyoutNotificationAnimation
          .animateTo(
            'opened',
            {
              onStart: () => this.flyoutNotification.style.setProperty('--notification-height', `${scrollheight}px`),
              force: !animate,
            },
          )
      ));
    }

    return animation.then(() => {
      if (timeout > 0) {
        setTimeout(() => this.clearNotification(true), timeout);
      }
    });
  }

  /**
   * Updates the content of the flyout to contain the DOM in contentHTML. If the element is already
   * in the DOM, that element will be moved inside of this flyout and will no longer exist in its
   * original location.
   *
   * @param {HTMLNode} contentNode The content to be placed in the flyout (will be moved in the DOM)
   */
  populateContent(contentNode, replace = false) {
    if (replace) {
      this.flyoutContent.innerHTML = '';
    }

    this.flyoutContent.appendChild(contentNode);

    if (this.state.open) {
      // Trap focus again so that all items within the content are now accessible through keyboard
      Core.A11y.trapFocus(this.el);
    }
  }

  populateFooter(contentNode, replace = false) {
    if (replace) {
      this.flyoutFooter.innerHTML = '';
    }

    this.flyoutFooter.appendChild(contentNode);
    this.flyoutFooter.removeAttribute('hidden');

    if (this.state.open) {
      // Trap focus again so that all items within the content are now accessible through keyboard
      Core.A11y.trapFocus(this.el);
    }
  }

  /**
   * Closes this flyout, and then removes the trap focus from it.
   */
  close() {
    this.setState({
      open: false,
      trigger: null,
    });

    return this.flyoutAnimation
      .animateTo('closed')
      .then(() => {
        Core.Utils.scrollUnlock(this.el);
        Core.A11y.removeTrapFocus();
      });
  }

  destroy() {
    this.el.remove();
  }
}
